/**
 * The return path that survives the emailed sign-in link (ORDR-189).
 *
 * `next=` cannot ride in the link. paio-auth builds it as
 * `link_base + "/" + token` and holds `link_base` to the manifest's callback
 * hosts, so a path in it would be either refused or, worse, a second place a
 * redirect target is accepted from. The path is put down here when the link
 * is requested and taken on `/auth/link/<token>`.
 *
 * LOCAL, NOT SESSION, STORAGE. The emailed link is usually opened from a mail
 * client into a NEW tab, where sessionStorage starts empty and the path would
 * be lost every time. localStorage reaches that tab in the same browser; a
 * link opened in another browser finds nothing and lands on the default, which
 * is correct. Because localStorage outlives the tab, the entry carries an
 * expiry a little longer than the link's own ten minutes, and is taken once.
 *
 * STORAGE IS NOT A TRUST BOUNDARY, as `returnPath.ts` says of `next=`.
 * `safeReturnPath` runs on the way in and again on the way out, and every
 * access is wrapped: a sign-in that otherwise worked is not failed because
 * storage is switched off.
 */

import { safeReturnPath } from "./returnPath.js";

const STORAGE_KEY = "paio.ordering.linkReturnPath";

/** paio-auth's link works for ten minutes; allow for a slow mail delivery. */
const LIFETIME_MS = 30 * 60 * 1000;

interface StoredReturnPath {
  readonly path: string;
  readonly expiresAt: number;
}

function parse(raw: string | null): StoredReturnPath | null {
  if (raw === null) {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (value === null || typeof value !== "object") {
    return null;
  }
  const { path, expiresAt } = value as Record<string, unknown>;
  return typeof path === "string" && typeof expiresAt === "number" ? { path, expiresAt } : null;
}

/** Put the validated return path down before the link is requested. */
export function writeLinkReturnPath(path: string): void {
  const entry: StoredReturnPath = { path: safeReturnPath(path), expiresAt: Date.now() + LIFETIME_MS };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage switched off: the link still signs in, to the default landing.
  }
}

/** The validated path to go to after the link signs in, taken once. */
export function takeLinkReturnPath(): string {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing readable; fall through to the default.
  }
  const entry = parse(raw);
  if (entry === null || entry.expiresAt < Date.now()) {
    return safeReturnPath(null);
  }
  return safeReturnPath(entry.path);
}
